import React from 'react';
import { Alert, Icon, Typography } from '@/components/common';
import { Checkbox } from '@/components/form';
import Icons from '@/const/icons';
import { ReplyPanelHeader } from './ReplyPanelHeader';
import { ReplyToClientIntro } from './ReplyToClientIntro';
import { LastMessageBubble } from './LastMessageBubble';
import { DocumentLastMessage } from '@/interfaces/clientDocumentation';

interface Props {
	backLabel: string;
	onBack: () => void;
	documentUrl: string;
	fileName?: string;
	documentTitle?: string;
	downloadDisabled?: boolean;
	pastDaysLabel?: string | null;
	lastMessage: DocumentLastMessage | null | undefined;
	showReplyForm: boolean;
	message: string;
	onMessageChange: (value: string) => void;
	onSendMessage: () => void;
	requestResubmit: boolean;
	onRequestResubmitChange: (checked: boolean) => void;
}

export const ReplyPanel: React.FC<Props> = ({
	backLabel,
	onBack,
	documentUrl,
	fileName,
	documentTitle,
	downloadDisabled,
	pastDaysLabel,
	lastMessage,
	showReplyForm,
	message,
	onMessageChange,
	onSendMessage,
	requestResubmit,
	onRequestResubmitChange,
}) => (
	<div className="border border-grey-100 rounded-t-2xl h-full flex flex-col p-4">
		<ReplyPanelHeader
			backLabel={backLabel}
			onBack={onBack}
			documentUrl={documentUrl}
			fileName={fileName}
			downloadDisabled={downloadDisabled}
		/>
		<ReplyToClientIntro />
		<div
			className={
				showReplyForm
					? 'bg-purple-35 py-3 px-2 rounded-lg flex flex-col'
					: 'bg-purple-35 py-3 px-2 rounded-lg'
			}
		>
			<div className="bg-gray-15 rounded-tr-2xl rounded-b-2xl border-l-2 border-primary p-3 flex items-center justify-between">
				<div className="flex items-center gap-2">
					<Icon
						src={Icons.documentFile}
						width={16}
						height={16}
						className="text-green-60 shrink-0"
					/>
					<div>
						<Typography type="regular-14" className="!text-left">
							{documentTitle}
						</Typography>
						{pastDaysLabel && (
							<Typography type="regular-12" className="text-gray-35 !text-left">
								{pastDaysLabel}
							</Typography>
						)}
					</div>
				</div>
			</div>

			<LastMessageBubble
				lastMessage={lastMessage}
				className={
					showReplyForm
						? 'max-h-[180px] overflow-y-auto scroll-custom'
						: undefined
				}
			/>

			{showReplyForm && (
				<div className="relative mt-3 flex items-end gap-2">
					<textarea
						value={message}
						onChange={(e) => onMessageChange(e.target.value)}
						placeholder="Message client"
						className="w-full rounded-[32px] p-3 pr-12 resize-none min-h-[120px] outline-none bg-white"
						rows={5}
					/>
					<button
						type="button"
						onClick={onSendMessage}
						disabled={!message.trim()}
						className="bg-primary text-white rounded-full p-2 size-12 shrink-0 disabled:opacity-50 hover:opacity-90 transition-opacity flex items-center justify-center"
					>
						<Icon src={Icons.sendEmail} width={16} height={16} />
					</button>
				</div>
			)}
		</div>

		{showReplyForm && (
			<>
				<div className="flex items-center gap-2 mt-4">
					<Checkbox
						checked={requestResubmit}
						onChange={(e) => onRequestResubmitChange(e.target.checked)}
					/>
					<Typography type="regular-14">Request client to resubmit</Typography>
				</div>

				{requestResubmit && (
					<Alert
						type="info"
						title="Note"
						hideIcon
						description={
							<Typography type="regular-14" className="text-grey-90 text-left">
								The document&apos;s status will change to &quot;
								<span className="font-bold">Pending</span>&quot; until the
								client resubmits. You&apos;ll be notified when they do.
							</Typography>
						}
						titleClassName="!text-black"
						className="mt-4 !p-4 !rounded-2xl border border-main-blue-dark !text-left"
					/>
				)}
			</>
		)}
	</div>
);
